<template>
    <div>
        <GoogleMap
            ref="map"
            :key="`route-planner-${mapId}`"
            :map-id="mapId"
            :ask-for-user-location="askForUserLocation"
            :initial-map-lat="initialMapLat"
            :initial-map-lng="initialMapLng"
            :default-zoom="defaultZoom"
            :design="design"
            :map-route="mapRoute"
            @status-update="onStatusUpdate($event)"
            @routes-updated="onRoutesUpdate($event)"
            @route-selected="onRouteSelection($event)"
            @requirements-updated="onRequirementUpdate($event)"
            @scroll-to-feature-form="scrollToFeatureForm"
        />

        <section v-if="status.ready || status.loading || status.failed" class="directions">
            <div class="container feature-form" ref="featureForm">
                <div v-if="status.failed" class="failed">
                    <div class="alert alert-warning mb-0" role="alert">
                        <i class="mdi mdi-alert"></i>
                        <span v-html="$t('directions.alert_failed')"></span>
                    </div>
                </div>
                <Directions
                    v-else-if="!status.loading"
                    :status="status"
                    :routes="routes"
                    :product-route="productRoute"
                    :products="requirements.products"
                    :active-route="active.id"
                    :active-back-route="activeBack.id"
                    :design="design"
                    :routeplaner-show-products="routeplanerShowProducts"
                    :combined-order-route="combinedOrderRoute"
                    :show-calendar-as-result="showCalendarAsResult"
                    :max-validity-start="maxValidityStart"
                    :max-validity-end="maxValidityEnd"
                    @select-route="onSelectRoute($event, false)"
                    @select-route-back="onSelectRoute($event, true)"
                />
            </div>
        </section>
    </div>
</template>

<script>
import Directions from './Directions.vue';
import Map from './Map.vue';

export default {
    components: {
        Directions,
        GoogleMap: Map,
    },

    props: {
        mapId: {
            type: String,
            required: true,
        },
        productRoute: {
            type: String,
            required: true,
        },
        isOfferSystemEnabled: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        askForUserLocation: {
            type: Boolean,
            required: false,
            default() {
                return false;
            },
        },
        initialMapLat: {
            type: Number,
            required: false,
            default() {
                return 47.6928623;
            },
        },
        initialMapLng: {
            type: Number,
            required: false,
            default() {
                return 12.2247775;
            },
        },
        defaultZoom: {
            type: Number,
            required: false,
            default() {
                return 13;
            },
        },
        design: {
            type: String,
            required: false,
        },
        mapRoute: {
            type: String,
            required: false,
        },
        routeplanerShowProducts: {
            type: Boolean,
            require: false,
        },
        combinedOrderRoute: {
            type: String,
            require: false,
        },
        showCalendarAsResult: {
            type: Boolean,
            require: false,
            default: false,
        },
        maxValidityStart: {
            type: String,
            required: false,
        },
        maxValidityEnd: {
            type: String,
            required: false,
        },
    },

    data() {
        return {
            status: {
                initial: true,
                ready: false,
                loading: false,
                failed: false,
                hasRouteBack: false,
            },
            routes: {
                outward: [],
                back: [],
            },
            active: {
                id: null,
                route: null,
            },
            activeBack: {
                id: null,
                route: null,
            },
            requirements: {
                isVignetteRequired: false,
                isRouteTollRequired: false,
                products: {
                    vignettes: [],
                    routeTolls: [],
                },
                systems: {},
            },
        };
    },

    methods: {
        /**
         * @param {object} status
         *
         * @returns {void}
         */
        onStatusUpdate(status) {
            this.status = status;
        },

        /**
         * Handle the update of the available routes.
         *
         * @param {object[]} routes
         *
         * @returns {void}
         */
        onRoutesUpdate(routes) {
            const outward = [],
                back = [];

            for (const route of routes) {
                if (typeof route.back === 'boolean' && route.back) {
                    back.push(route);
                    continue;
                }
                outward.push(route);
            }

            this.routes.outward = outward;
            this.routes.back = back;
        },

        /**
         * Handle the selection callback of the route.
         *
         * @param {object} selected
         *
         * @returns {void}
         */
        onRouteSelection(selected) {
            if (typeof selected.isBack === 'boolean' && selected.isBack) {
                this.activeBack = selected.route;
            } else {
                this.active = selected.route;
            }
        },

        /**
         * @param {object} requirements
         *
         * @returns {void}
         */
        onRequirementUpdate(requirements) {
            this.$set(this, 'requirements', requirements);
        },

        /**
         * Handles the update of the selected route.
         *
         * @param {object} route
         *
         * @returns {void}
         */
        onSelectRoute(route) {
            this.$refs.map.selectRoute(route, route.id);
        },

        scrollToFeatureForm() {
            this.$nextTick(() => {
                if (this.$refs.featureForm) {
                    const offsetTop = this.$refs.featureForm.getBoundingClientRect().top + window.scrollY - 150;
                    window.scrollTo({ top: offsetTop, behavior: 'smooth' });
                }
            });
        },
    },
};
</script>
